import { Component, computed, inject, OnInit } from '@angular/core';
import { SharedModule } from '../../../shared/shared.module';
import { FormFieldsetComponent } from '../../../shared/components/form-fieldset.component';
import { Store } from '@ngrx/store';
import { customFieldsActions } from 'angular/app/core/store/custom-fields';
import {
  selectAllCustomFields,
  selectAllRooms,
  selectAuthUser,
  selectCustomFieldsLoading,
} from 'angular/app/core/store/reducers';
import { ModalService } from '../../../core/services/modal.service';
import { CustomFieldFormModal } from '../../components/create-custom-field-modal/custom-field-form-modal.component';
import { roomsActions } from 'angular/app/core/store/rooms';
import { CustomField } from '../../../core/models/entities/custom-field';
import { CustomFieldsService } from '../../../core/services/custom-fields.service';
import { ToastService } from '../../../core/services/toast.service';
import { ApiError } from '../../../core/models/common/api-error';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { take } from 'rxjs';
import { BookableBy } from '../../../core/models/common/bookable-by';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { SharedManagerModule } from '../../../shared-manager/shared-manager.module';

@Component({
  selector: 'app-custom-fields-page',
  imports: [
    SharedModule,
    FormFieldsetComponent,
    TooltipDirective,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    SharedManagerModule,
  ],
  styles: `
    .sortable-item {
      display: inline-block;
      width: 100%;
    }

    .cdk-drag-placeholder {
      opacity: 0;
    }

    .cdk-drag-animating {
      transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
    }

    .sortable-list.cdk-drop-list-dragging
      .sortable-item:not(.cdk-drag-placeholder) {
      transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
    }
  `,
  template: `
    <app-page-header title="Champs personnalisés">
      <ng-template #actions>
        <app-button variant="primary" (click)="addCustomField()">
          Ajouter
        </app-button>
      </ng-template>
    </app-page-header>

    <app-spacer size="6"></app-spacer>

    @if (loading()) {
      <app-loader [centered]="true"></app-loader>
    } @else {
      <app-fieldset>
        <div title>Champs personnalisés</div>
        <div description
          >Vous pouvez créer des champs à afficher dans le formulaire de
          réservation de vos salles. Il est possible de définir les salles
          concernées ainsi que d’indiquer à quel type de réservation le champ
          doit s'appliquer.</div
        >

        <div>
          @if (customFields().length === 0) {
            <app-empty-state
              icon="input-text"
              message="Vous n'avez pas encore créé de champ personnalisé."
              [inline]="true"
              [cardStyle]="true"
              (buttonClicked)="addCustomField()"
            ></app-empty-state>
          } @else {
            <div
              class="sortable-list space-y-6"
              cdkDropList
              (cdkDropListDropped)="drop($event)"
            >
              @for (customField of customFields(); track customField.id) {
                <div>
                  <app-list-item
                    cdkDrag
                    cdkDragLockAxis="y"
                    class="sortable-item"
                  >
                    <app-toggle
                      [defaultValue]="$any(customField).enabled"
                      (isEnabled)="toggleEnabled($any(customField), $event)"
                      toggle
                    ></app-toggle>

                    <div title>
                      <div class="font-bold"
                        >{{ customField.title }} ({{
                          customField.is_required ? 'Obligatoire' : 'Optionnel'
                        }})</div
                      >
                      <div class="text-gray-500">
                        @if (customField.visibility === BookableBy.Both) {
                          Toutes les réservations
                        } @else if (
                          customField.visibility === BookableBy.Private
                        ) {
                          Réservations internes
                        } @else {
                          Réservations publiques
                        }
                        ·
                        @if (!customField.room_ids) {
                          Toutes les salles
                        } @else {
                          <span
                            type="button"
                            class="cursor-help"
                            [tooltip]="tooltips()[$index]"
                            >Limité à certaines salles</span
                          >
                        }
                      </div>
                    </div>
                    <div actions class="space-x-4">
                      <app-icon-action
                        icon="pen"
                        [attr.data-cy]="'edit'"
                        (click)="editCustomField(customField)"
                      ></app-icon-action>
                      <app-icon-action
                        icon="trash"
                        [attr.data-cy]="'delete'"
                        (click)="deleteCustomField(customField)"
                      ></app-icon-action>
                      <app-icon-action
                        icon="grip-vertical"
                        cdkDragHandle
                      ></app-icon-action>
                    </div>
                  </app-list-item>
                </div>
              }
            </div>
          }
        </div>
      </app-fieldset>
    }
  `,
})
export class CustomFieldsPageComponent implements OnInit {
  protected readonly BookableBy = BookableBy;
  store = inject(Store);
  modalService = inject(ModalService);
  toastService = inject(ToastService);
  customFieldsService = inject(CustomFieldsService);
  loading = this.store.selectSignal(selectCustomFieldsLoading);
  customFields = this.store.selectSignal(selectAllCustomFields);
  rooms = this.store.selectSignal(selectAllRooms);
  authUser = this.store.selectSignal(selectAuthUser);
  tooltips = computed(() => {
    return this.customFields().map((customField) => {
      return customField.room_ids
        ? customField.room_ids
            .map((roomId) => {
              const room = this.rooms().find((r) => r.id === roomId);
              return room ? room.name : '';
            })
            .sort((a, b) => a.localeCompare(b))
            .join('<br>')
        : '';
    });
  });

  ngOnInit() {
    this.store.dispatch(
      customFieldsActions.findAll({ vendorId: this.authUser().vendor.id }),
    );
    if (this.rooms()?.length === 0) {
      this.store.dispatch(roomsActions.findAll({ page: 1 }));
    }
  }

  async drop(event: CdkDragDrop<string[]>) {
    const previousOrder = [...this.customFields()];

    moveItemInArray(
      this.customFields(),
      event.previousIndex,
      event.currentIndex,
    );

    try {
      await this.savePositions();
      // Mettre à jour le state avec chaque position mise à jour
      this.store.dispatch(
        customFieldsActions.findAllSuccess({
          customFields: this.customFields().map((customField, index) => ({
            ...customField,
            position: index,
          })),
        }),
      );
    } catch (exception) {
      this.store.dispatch(
        customFieldsActions.findAllSuccess({ customFields: previousOrder }),
      );
      this.toastService.error(
        (exception as ApiError).message,
        'Impossible de réordonner les éléments',
      );
    }
  }

  private async savePositions() {
    return this.customFieldsService.updatePositions(
      this.customFields().map((customField, index) => ({
        id: customField.id,
        position: index,
      })),
    );
  }

  addCustomField() {
    this.modalService.open(CustomFieldFormModal, {
      size: 'lg',
      title: 'Ajouter un champ personnalisé',
    });
  }

  async toggleEnabled(customField: CustomField, isEnabled: boolean) {
    try {
      const customFieldUpdated = await this.customFieldsService.updateStatus(
        customField.id,
        isEnabled,
      );
      this.store.dispatch(
        customFieldsActions.upsertOne({ customField: customFieldUpdated }),
      );
    } catch (exception) {
      this.toastService.error((exception as ApiError).message);
      this.store.dispatch(
        customFieldsActions.upsertOne({
          customField: { ...customField, enabled: !isEnabled },
        }),
      );
    }
  }

  editCustomField(customField: CustomField) {
    const modalRef = this.modalService.open(CustomFieldFormModal, {
      size: 'lg',
      title: 'Modifier un champ personnalisé',
    });
    (
      modalRef.componentRef.instance as CustomFieldFormModal
    ).setEditingCustomField(customField);
  }

  deleteCustomField(customField: CustomField) {
    const instance = this.modalService.open(ConfirmModalComponent, {
      title: 'Supprimer champ personnalisé',
      message: `Êtes-vous sûr de vouloir supprimer le champ personnalisé "${customField.title}" ?`,
      okAction: {
        title: 'Supprimer',
        variant: 'danger',
      },
    });
    instance.okClicked$.pipe(take(1)).subscribe(async () => {
      try {
        instance.saving = true;
        await this.customFieldsService.delete(customField.id);
        this.store.dispatch(
          customFieldsActions.removeOne({ id: customField.id }),
        );
        this.toastService.success(
          'Le champ personnalisé a été supprimé avec succès.',
        );
        this.modalService.close();
      } catch (exception) {
        this.toastService.error((exception as ApiError).message);
      } finally {
        instance.saving = false;
      }
    });
  }
}
